/**
 * SDMS Template Builder — Conditional Formatting Rules Manager (the Excel / DevExpress
 * dialog): the rules for the CURRENT SELECTION or a whole worksheet, in evaluation order,
 * with New / Edit / Delete, Up / Down and Stop If True. Works on a copy of the rule list;
 * OK hands the edited list back to the editor (written to the .xlsx on Save), Cancel
 * throws it away.
 */
import { useEffect, useMemo, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { useConfirm } from '../../../components/confirm';
import {
  absoluteSqref, describeRule, EDITABLE_CF_TYPES, newRule, sqrefIntersects,
  type CellBox, type CfDefinition, type CfRule,
} from './cfModel';
import { ConditionalFormatDialog } from './ConditionalFormatDialog';

const SELECTION = 'sel';

export function ConditionalFormatManager({ open, newRuleOnly, sheetNames, rules, selection, onApply, onClose, readOnly }: {
  open: boolean;
  /** Ribbon "New Rule…": show only the rule dialog; OK adds the rule, Cancel closes (Excel parity). */
  newRuleOnly?: boolean;
  /** worksheet names in workbook order (index = position + 1) */
  sheetNames: string[];
  rules: CfRule[];
  /** the sheet selection when the manager opened (null = none) */
  selection: ({ sheetIndex: number } & CellBox) | null;
  onApply: (rules: CfRule[]) => void;
  onClose: () => void;
  readOnly?: boolean;
}) {
  const { alert, confirm } = useConfirm();
  const [work, setWork] = useState<CfRule[]>([]);
  const [scope, setScope] = useState<string>(SELECTION);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [editor, setEditor] = useState<{ mode: 'new' | 'edit'; sheetIndex: number; initial: CfDefinition } | null>(null);

  useEffect(() => {
    if (!open) return;
    setWork(rules);
    setScope(selection ? SELECTION : String(1));
    setActiveKey(null);
    setEditor(newRuleOnly ? { mode: 'new', sheetIndex: selection?.sheetIndex ?? 1, initial: newDefinition(selection ? boxSqref(selection) : '') } : null);
  }, [open, rules, selection, newRuleOnly]);

  const scopeSheet = scope === SELECTION ? selection?.sheetIndex ?? 1 : Number(scope);
  const visible = useMemo(() => work.filter(r => r.sheetIndex === scopeSheet
    && (scope !== SELECTION || !selection || sqrefIntersects(r.range, selection))), [work, scope, scopeSheet, selection]);
  const active = work.find(r => r.key === activeKey) ?? null;
  const sheetName = (i: number) => sheetNames[i - 1] ?? `Sheet${i}`;
  const selectionSqref = selection ? boxSqref(selection) : '';

  const openNew = () => setEditor({ mode: 'new', sheetIndex: scopeSheet, initial: newDefinition(scope === SELECTION ? selectionSqref : '') });
  const openEdit = () => {
    if (!active) return;
    if (!EDITABLE_CF_TYPES.has(active.type)) {
      void alert({ title: 'Edit Rule', message: `"${describeRule(active)}" rules can be reordered or deleted here but not edited.`, tone: 'info' });
      return;
    }
    setEditor({ mode: 'edit', sheetIndex: active.sheetIndex, initial: active });
  };
  const onEditorDone = (def: CfDefinition) => {
    if (!editor) return;
    if (editor.mode === 'new') {
      const rule = newRule(editor.sheetIndex, def);
      // A new rule goes FIRST in its sheet, as Excel does — it wins over the older rules.
      const i = work.findIndex(r => r.sheetIndex === editor.sheetIndex);
      const next = i < 0 ? [...work, rule] : [...work.slice(0, i), rule, ...work.slice(i)];
      if (newRuleOnly) { setEditor(null); onApply(next); return; }
      setWork(next);
      setActiveKey(rule.key);
    } else {
      setWork(w => w.map(r => (r.key === activeKey ? { ...r, ...def } : r)));
    }
    // A rule whose range is outside the selection would vanish from "Current Selection" — show its sheet instead.
    if (scope === SELECTION && selection && !sqrefIntersects(def.range, selection)) setScope(String(editor.sheetIndex));
    setEditor(null);
  };
  const remove = async () => {
    if (!active) return;
    if (!(await confirm({ title: 'Delete Rule', message: `Delete the rule "${describeRule(active)}" for ${absoluteSqref(active.range)}?`, tone: 'danger' }))) return;
    setWork(w => w.filter(r => r.key !== active.key));
    setActiveKey(null);
  };
  /** Swap with the previous / next VISIBLE rule of the same sheet (Excel moves within the shown list). */
  const move = (dir: -1 | 1) => {
    if (!active) return;
    const vi = visible.findIndex(r => r.key === active.key);
    const other = visible[vi + dir];
    if (!other) return;
    setWork(w => {
      const a = w.findIndex(r => r.key === active.key), b = w.findIndex(r => r.key === other.key);
      const next = [...w];
      [next[a], next[b]] = [next[b]!, next[a]!];
      return next;
    });
  };
  const toggleStop = (key: string, on: boolean) => setWork(w => w.map(r => (r.key === key ? { ...r, stopIfTrue: on } : r)));

  const vIndex = active ? visible.findIndex(r => r.key === active.key) : -1;

  const columns: AppColumn<CfRule>[] = [
    { field: 'rule', header: 'Rule (applied in order shown)', width: 280, sortable: false, value: r => describeRule(r), text: r => describeRule(r) },
    {
      field: 'format', header: 'Format', width: 150, sortable: false, filterable: false, noExport: true,
      render: r => (EDITABLE_CF_TYPES.has(r.type) || r.fontColor || r.fillColor
        ? <span className="stb-cf-sample" style={{ color: r.fontColor ?? undefined, background: r.fillColor ?? undefined, fontWeight: r.bold ? 700 : undefined }}>AaBbCcYyZz</span>
        : <span className="stb-muted">{describeRule(r)}</span>),
    },
    { field: 'range', header: 'Applies to', width: 180, sortable: false, value: r => absoluteSqref(r.range), text: r => absoluteSqref(r.range) },
    {
      field: 'stopIfTrue', header: 'Stop If True', width: 100, sortable: false, filterable: false, align: 'center',
      value: r => (r.stopIfTrue ? 'Yes' : 'No'),
      render: r => (
        <input type="checkbox" checked={r.stopIfTrue} disabled={readOnly} aria-label="Stop If True"
          onClick={e => e.stopPropagation()} onChange={e => toggleStop(r.key, e.target.checked)} />
      ),
    },
  ];

  return (
    <>
      <AppDialog open={open && !newRuleOnly} title="Conditional Formatting Rules Manager" icon="ti-color-filter" width={880} onClose={onClose}
        submitOnEnter={false}
        footer={<>
          <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="ca-btn ca-btn-solid" disabled={readOnly} onClick={() => onApply(work)}>OK</button>
        </>}>
        <div className="stb-cfm-scope">
          <label htmlFor="cfm-scope">Show formatting rules for:</label>
          <select id="cfm-scope" className="sd-input" value={scope} onChange={e => { setScope(e.target.value); setActiveKey(null); }}>
            {selection && <option value={SELECTION}>Current Selection ({sheetName(selection.sheetIndex)}!{selectionSqref})</option>}
            {sheetNames.map((n, i) => <option key={n} value={String(i + 1)}>This Worksheet: {n}</option>)}
          </select>
        </div>
        <div className="stb-actions">
          <button type="button" className="ca-btn" disabled={readOnly} onClick={openNew}><i className="ti ti-plus" /> New Rule…</button>
          <button type="button" className="ca-btn" disabled={readOnly || !active} onClick={openEdit}><i className="ti ti-edit" /> Edit Rule…</button>
          <button type="button" className="ca-btn" disabled={readOnly || !active} onClick={() => void remove()}><i className="ti ti-trash" /> Delete Rule</button>
          <button type="button" className="ca-btn" disabled={readOnly || vIndex <= 0} onClick={() => move(-1)} title="Evaluate earlier"><i className="ti ti-arrow-up" /> Up</button>
          <button type="button" className="ca-btn" disabled={readOnly || vIndex < 0 || vIndex >= visible.length - 1} onClick={() => move(1)} title="Evaluate later"><i className="ti ti-arrow-down" /> Down</button>
        </div>
        <div className="stb-cfm-grid">
          <AppDataGrid<CfRule> rows={visible} columns={columns} getRowId={r => r.key} defaultPageSize={50} hideFooter
            hideHeaderTools selectedRowId={activeKey ?? undefined}
            onRowClick={r => setActiveKey(r.key)} onRowDoubleClick={r => { setActiveKey(r.key); if (!readOnly) setTimeout(openEdit, 0); }}
            emptyText={scope === SELECTION ? 'No rules apply to the selected cells.' : 'No rules on this worksheet.'} />
        </div>
      </AppDialog>
      <ConditionalFormatDialog open={editor != null} mode={editor?.mode ?? 'new'} sheetName={sheetName(editor?.sheetIndex ?? 1)}
        initial={editor?.initial ?? EMPTY_DEF} onDone={onEditorDone}
        onClose={() => { setEditor(null); if (newRuleOnly) onClose(); }} />
    </>
  );
}

const EMPTY_DEF: CfDefinition = { range: '', type: 'cellIs', operator: 'greaterThan', formula1: null, formula2: null, text: null, fontColor: null, fillColor: null, bold: false };

/** A new rule's starting point: Excel's "Light Red Fill with Dark Red Text". */
const newDefinition = (range: string): CfDefinition => ({ ...EMPTY_DEF, range, fontColor: '#9C0006', fillColor: '#FFC7CE' });

const boxSqref = (b: CellBox) =>
  `${cell(b.startRow, b.startCol)}${b.endRow !== b.startRow || b.endCol !== b.startCol ? `:${cell(b.endRow, b.endCol)}` : ''}`;

function cell(row0: number, col0: number): string {
  let n = col0 + 1;
  let s = '';
  for (; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return `${s}${row0 + 1}`;
}
